import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ImageUploader } from '../../components/admin/ImageUploader';
import { toast } from 'sonner';
import { Pencil, Loader2, Save, ExternalLink, Search, Plus, Trash2, X } from 'lucide-react';

const inputCls = 'mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/35';

const emptyCourse = {
  agency: 'SDI',
  slug: '',
  name: '',
  short_name: '',
  category: 'specialty',
  description: '',
  youtube_url: '',
  register_url: '',
  hero_image_url: '',
  gallery: [],
  parent_slug: null,
  order: 100,
  active: true,
};

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 80);

export default function CoursesTab() {
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState('SDI');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get('/courses/all');
    setCourses(data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return courses
      .filter((c) => c.agency === filter)
      .filter((c) => !q || c.name.toLowerCase().includes(q) || (c.slug || '').toLowerCase().includes(q))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [courses, filter, search]);

  const startNew = () => {
    setEditing({ ...emptyCourse, agency: filter });
    setIsNew(true);
    setOpen(true);
  };

  const startEdit = (c) => {
    setEditing({ ...c, gallery: Array.isArray(c.gallery) ? c.gallery : [] });
    setIsNew(false);
    setOpen(true);
  };

  const save = async () => {
    if (!editing.name?.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name,
        short_name: editing.short_name || editing.name,
        description: editing.description || '',
        youtube_url: editing.youtube_url || '',
        register_url: editing.register_url || '',
        hero_image_url: editing.hero_image_url || '',
        gallery: editing.gallery || [],
        active: editing.active !== false,
        order: Number(editing.order ?? 100),
      };
      if (isNew) {
        await api.post('/courses', {
          ...payload,
          agency: editing.agency,
          slug: editing.slug || slugify(editing.name),
          category: editing.category || 'specialty',
          parent_slug: editing.parent_slug || null,
        });
        toast.success('Course created');
      } else {
        await api.patch(`/courses/${editing.id}`, payload);
        toast.success('Course updated');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing?.id) return;
    if (!window.confirm(`Delete "${editing.name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api.delete(`/courses/${editing.id}`);
      toast.success('Course deleted');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const addGalleryImage = (url) => {
    if (!url) return;
    setEditing((e) => ({ ...e, gallery: [...(e.gallery || []), url] }));
  };

  const removeGalleryAt = (idx) => {
    setEditing((e) => ({ ...e, gallery: (e.gallery || []).filter((_, i) => i !== idx) }));
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 justify-between mb-5">
        <div>
          <div className="font-display text-xl font-semibold">Courses</div>
          <div className="text-xs text-white/55">Add/edit courses, descriptions, photos (multiple), YouTube videos, and registration links</div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-xl border border-white/12 overflow-hidden text-sm">
            {['SDI', 'TDI', 'FRTI'].map((a) => (
              <button key={a} onClick={() => setFilter(a)} data-testid={`admin-courses-filter-${a.toLowerCase()}`} className={`px-4 py-2 ${filter === a ? 'bg-[#00b4d8]/15 text-[#00b4d8]' : 'text-white/65 hover:text-white hover:bg-white/5'}`}>{a}</button>
            ))}
          </div>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
            <Input data-testid="admin-courses-search" placeholder="Search by name…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-56 bg-white/5 border-white/15 text-white placeholder:text-white/35" />
          </div>
          <Button onClick={startNew} data-testid="admin-course-new-button" className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] font-semibold">
            <Plus className="mr-1.5 h-4 w-4" /> New course
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((c) => (
          <Card key={c.id} className="rounded-2xl border-white/10 bg-white/[0.04] p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-white/45">{(c.category || '').replace('_', ' ')}</div>
                <div className="font-display font-semibold leading-tight truncate">{c.name}</div>
                <div className="text-[11px] text-white/40 mt-0.5 truncate">{c.slug}</div>
              </div>
              <Button size="sm" variant="outline" data-testid="admin-course-edit" onClick={() => startEdit(c)} className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-lg">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="mt-3 flex items-center gap-3 text-[11px] text-white/45 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full border ${c.active ? 'border-emerald-400/40 text-emerald-300' : 'border-red-400/40 text-red-300'}`}>{c.active ? 'Visible' : 'Hidden'}</span>
              {c.youtube_url && <a href={c.youtube_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white">video <ExternalLink className="h-3 w-3" /></a>}
              {c.register_url && <a href={c.register_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-white">register <ExternalLink className="h-3 w-3" /></a>}
              {Array.isArray(c.gallery) && c.gallery.length > 0 && (
                <span className="text-white/55">{c.gallery.length} photos</span>
              )}
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-white/55 text-sm p-10 text-center rounded-3xl border border-white/10 bg-white/5">No courses match.</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-[#07101f] border-white/12 text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{isNew ? `New ${editing?.agency || ''} course` : 'Edit course'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto scrollbar-thin pr-1">
              {isNew && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Agency</Label>
                    <Select value={editing.agency} onValueChange={(v) => setEditing({ ...editing, agency: v })}>
                      <SelectTrigger className={`${inputCls} w-full`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#07101f] border-white/15 text-white">
                        {['SDI', 'TDI', 'FRTI'].map((a) => (
                          <SelectItem key={a} value={a} className="focus:bg-white/10 focus:text-white">{a}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Input className={inputCls} placeholder="specialty / core / first-aid …" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Display name *</Label>
                  <Input
                    className={inputCls}
                    value={editing.name}
                    onChange={(e) => {
                      const n = e.target.value;
                      setEditing({ ...editing, name: n, slug: isNew && !editing.slug ? slugify(n) : editing.slug });
                    }}
                  />
                </div>
                <div>
                  <Label>Short name (used in flowchart node)</Label>
                  <Input className={inputCls} value={editing.short_name || ''} onChange={(e) => setEditing({ ...editing, short_name: e.target.value })} />
                </div>
              </div>

              {isNew && (
                <div>
                  <Label>Slug (URL-safe, auto-generated from name)</Label>
                  <Input className={inputCls} value={editing.slug || ''} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} placeholder="auto-from-name" />
                </div>
              )}

              <div>
                <Label>YouTube URL (intro video shown in modal)</Label>
                <Input data-testid="admin-course-youtube-input" className={inputCls} placeholder="https://www.youtube.com/watch?v=…" value={editing.youtube_url || ''} onChange={(e) => setEditing({ ...editing, youtube_url: e.target.value })} />
              </div>

              <ImageUploader
                label="Hero image (shown at top of modal when no video)"
                value={editing.hero_image_url}
                onChange={(v) => setEditing({ ...editing, hero_image_url: v })}
              />

              <div>
                <Label>Photo gallery — multiple photos shown after description</Label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {(editing.gallery || []).map((src, i) => (
                    <div key={i} className="relative aspect-[4/3] rounded-xl overflow-hidden border border-white/10 bg-white/5 group">
                      <img src={src} alt={`Photo ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeGalleryAt(i)}
                        className="absolute top-1.5 right-1.5 h-7 w-7 inline-flex items-center justify-center rounded-full bg-black/70 text-white hover:bg-red-500"
                        data-testid={`admin-course-remove-photo-${i}`}
                        title="Remove photo"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {(editing.gallery || []).length === 0 && (
                    <div className="col-span-3 text-xs text-white/45 italic py-2">No gallery photos yet — upload below.</div>
                  )}
                </div>
                <div className="mt-3">
                  <ImageUploader label="Upload a new photo to add to the gallery" value="" onChange={addGalleryImage} />
                </div>
              </div>

              <div>
                <Label>Register Now URL</Label>
                <Input data-testid="admin-course-register-input" className={inputCls} placeholder="https://… or wa.me/…" value={editing.register_url || ''} onChange={(e) => setEditing({ ...editing, register_url: e.target.value })} />
              </div>

              <div>
                <Label>Description (paragraphs, used in modal)</Label>
                <Textarea data-testid="admin-course-description-input" rows={10} className={inputCls} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Order (lower = first)</Label>
                  <Input className={inputCls} type="number" value={editing.order ?? 100} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} />
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex items-center gap-3">
                    <Switch checked={editing.active !== false} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                    <Label>Visible on site</Label>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-between gap-2 mt-2">
            {!isNew && editing?.id ? (
              <Button variant="ghost" onClick={handleDelete} disabled={saving} className="text-red-300 hover:text-red-200 hover:bg-red-500/10" data-testid="admin-course-delete">
                <Trash2 className="mr-1.5 h-4 w-4" /> Delete
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/65 hover:text-white hover:bg-white/5">Cancel</Button>
              <Button data-testid="admin-course-save-button" onClick={save} disabled={saving} className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100]">
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving</> : <><Save className="mr-2 h-4 w-4" /> Save</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
